// src/components/ui/Toast.tsx
// Toast notification system
// Base para NotificationCenter y UndoToast

import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  success: (message: string, title?: string, duration?: number) => void;
  error: (message: string, title?: string, duration?: number) => void;
  warning: (message: string, title?: string, duration?: number) => void;
  info: (message: string, title?: string, duration?: number) => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () =>
{
  const context = useContext(ToastContext);
  if (!context)
  {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

// Toast Provider
export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) =>
{
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>): string =>
  {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = { ...toast, id };

    setToasts(prev => [...prev, newToast]);

    // Auto remove after duration
    if (toast.duration !== 0)
    {
      setTimeout(() =>
      {
        removeToast(id);
      }, toast.duration || 5000);
    }

    return id;
  }, []);

  const removeToast = useCallback((id: string) =>
  {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const success = useCallback(
    (message: string, title?: string, duration?: number) =>
    {
      addToast({ type: 'success', message, title, duration });
    },
    [addToast],
  );

  const error = useCallback(
    (message: string, title?: string, duration?: number) =>
    {
      addToast({ type: 'error', message, title, duration });
    },
    [addToast],
  );

  const warning = useCallback(
    (message: string, title?: string, duration?: number) =>
    {
      addToast({ type: 'warning', message, title, duration });
    },
    [addToast],
  );

  const info = useCallback(
    (message: string, title?: string, duration?: number) =>
    {
      addToast({ type: 'info', message, title, duration });
    },
    [addToast],
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, warning, info }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

// Toast Container
interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) =>
{
  if (toasts.length === 0) return null;

  return createPortal(
    <div
      className="fixed z-[9999] flex flex-col gap-2 items-center"
      style={{ top: '60px', left: '50%', transform: 'translateX(-50%)' }}
    >
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>,
    document.body,
  );
};

// Toast Item
interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) =>
{
  const bgColors: Record<ToastType, string> = {
    success: '#62accb',
    error: '#c0392b',
    warning: '#e67e22',
    info: '#62accb',
  };

  return (
    <div
      className="flex items-center justify-center gap-3 px-8 py-2.5 rounded text-white text-sm animate-toast-slide-down"
      style={{
        backgroundColor: bgColors[toast.type],
        boxShadow: '0 3px 6px 0 rgba(0,0,0,.2)',
        minWidth: '320px',
      }}
      role="alert"
    >
      <span className="text-center">
        {toast.title && <span className="font-semibold mr-1">{toast.title}</span>}
        {toast.message}
      </span>

      {toast.action && (
        <button
          onClick={() =>
          {
            toast.action?.onClick();
            onRemove(toast.id);
          }}
          className="underline text-white opacity-90 hover:opacity-100 text-sm"
        >
          {toast.action.label}
        </button>
      )}
    </div>
  );
};

// Export individual toast functions for convenience
export const toast = {
  success: (_message: string, _title?: string, _duration?: number) =>
  {
    // This will be used via useToast hook
    console.warn('Use useToast hook to show toasts');
  },
  error: (_message: string, _title?: string, _duration?: number) =>
  {
    console.warn('Use useToast hook to show toasts');
  },
  warning: (_message: string, _title?: string, _duration?: number) =>
  {
    console.warn('Use useToast hook to show toasts');
  },
  info: (_message: string, _title?: string, _duration?: number) =>
  {
    console.warn('Use useToast hook to show toasts');
  },
};
